import { faPenField } from "@fortawesome/pro-solid-svg-icons"
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material"
import { useApiGet } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import SidebarInputLabel from "components/sidedrawer/SidebarInputLabel"
import { Formik, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useRef, useState } from "react"
import { StyledListItem } from "shared/styled/General"
import { vehicleAlarmNotificationValidationSchema } from "./VehicleValidationSchema"
import { VehicleAlarmNotificationDelete } from "./VehicleAlarmNotificationDelete"
import NonFormikSidebar from "components/sidedrawer/NonFormikSidebar"
import { StyledListItemHeaderGrid, StyledStickyButtonBox } from "components/sidedrawer/Sidebar.styled"
import DefaultButton from "components/Button"
import { CircleIcon } from "components/icons/CircleIcon"
import { t } from "i18next"
import { useTranslation } from "react-i18next"

const AlarmTypeFormSelect = ({ t }) => {
  const { values, setFieldValue, handleBlur, touched, errors } = useFormikContext()

  return (
    <StyledListItem>
      <Box width="100%">
        <SidebarInputLabel labelText={t("vehiclealarm.alarm_type", "Alarm type")} />
        <FormControl sx={{ width: "100%" }}>
          <Select
            MenuProps={{ disableScrollLock: true }}
            id="alarmType"
            value={Number(values?.alarmType) || ""}
            onChange={(e) => {
              setFieldValue("alarmType", Number(e.target.value))
            }}
            onBlur={handleBlur}
            error={touched.alarmType && errors.alarmType ? true : false}
            helperText={touched.alarmType && errors.alarmType ? errors.alarmType : null}>
            <MenuItem value={1}>{t("vehiclealarm.alcohol_lock", "Alcohol lock")}</MenuItem>
            <MenuItem value={2}>{t("vehiclealarm.drivers_cards", "Drivers cards")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </StyledListItem>
  )
}

const UserFormAutoSelect = ({ usersResult, t }) => {
  const { values, setFieldValue, handleBlur, touched, errors } = useFormikContext()
  return (
    <StyledListItem>
      <Box width="100%">
        <SidebarInputLabel labelText={t("global.user", "User")} />
        <FormControl sx={{ width: "100%" }}>
          {usersResult.status === 2 && (
            <Autocomplete
              id="userid-autocomplete"
              options={usersResult?.data}
              getOptionLabel={(option) => (option.name ? option.name : "")}
              value={usersResult.data.find((i) => i.id === values?.userId) || null}
              onChange={(e, value) => {
                setFieldValue("userId", value?.id || null)
                setFieldValue("user", value || null)
              }}
              onOpen={handleBlur}
              includeInputInList
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="userId"
                  variant="outlined"
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId ? true : false}
                  helperText={touched.userId && errors.userId ? errors.userId : null}
                />
              )}
            />
          )}
        </FormControl>
      </Box>
    </StyledListItem>
  )
}

const CompanyFormAutoSelect = ({ companyResult, t }) => {
  const { values, setFieldValue, handleBlur, touched, errors } = useFormikContext()

  return (
    <StyledListItem>
      <Box width="100%">
        <SidebarInputLabel labelText={t("global.company", "Company")} />
        <FormControl sx={{ width: "100%" }}>
          {companyResult.length > 0 && (
            <Autocomplete
              id="companyId-autocomplete"
              options={companyResult}
              getOptionLabel={(option) => (option.name ? option.name : "")}
              value={companyResult.find((i) => i.id === values?.companyId) || null}
              onChange={(e, value) => {
                setFieldValue("companyId", value?.id || null)
                setFieldValue("company", value || null)
              }}
              onOpen={handleBlur}
              includeInputInList
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="companyId"
                  variant="outlined"
                  onBlur={handleBlur}
                  error={touched.companyId && errors.companyId ? true : false}
                  helperText={touched.companyId && errors.companyId ? errors.companyId : null}
                />
              )}
            />
          )}
        </FormControl>
      </Box>
    </StyledListItem>
  )
}

export const alarmType = (value, t) => {
  switch (value) {
    case 1:
      return t("vehiclealarm.alcohol_lock", "Alcohol lock")
    default:
      return t("vehiclealarm.drivers_cards", "Drivers cards")
  }
}

export const VehicleAlarmNotificationForm = ({
  formData,
  onSubmit,
  editVehicleAlarmNotification,
  result,
  feedback,
  deleteFeedback,
  deleteNotificationSubmit,
  deleteResult
}) => {
  const appContext = useContext(AppContext)
  const { t } = useTranslation()

  const initialValues = editVehicleAlarmNotification
    ? formData
    : {
        companyId: "",
        userId: null,
        alarmType: ""
      }

  const [usersResult, getUsers] = useApiGet()
  useEffect(() => {
    getUsers(apiEndpoints.vehiclealarm.getresponsibleusersforvehiclealarmnotificationdropdown)
  }, [getUsers])

  const [companyResult] = useState(appContext.appUser.customer.companies)

  const handleSubmit = async (values) => {
    onSubmit(values)
  }

  const formRef = useRef()
  const theme = useTheme()

  return formData ? (
    <NonFormikSidebar
      sidebarId={editVehicleAlarmNotification ? "editAlarmNotification" : "addAlarmNotification"}
      buttonChildren={
        <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
          <DefaultButton
            sx={{ width: "100%" }}
            result={result}
            onClick={() => handleSubmit(formRef?.current?.values)}>
            {editVehicleAlarmNotification ? t("global.save_changes", "Save changes") : t("global.add", "Add")}
          </DefaultButton>
          <Button
            variant={"outlined"}
            onClick={() => {
              appContext.toggleSideBar(false)
            }}>
            {t("global.buttons.cancel")}
          </Button>
        </StyledStickyButtonBox>
      }>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={vehicleAlarmNotificationValidationSchema(t)}
        onSubmit={handleSubmit}
        enableReinitialize={true}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate style={{ height: "100%" }}>
            <Grid container pb={5} mt={0} pt={12}>
              <StyledListItemHeaderGrid flexDirection="column" alignItems="flex-start" xs={12} md={12} pb={3}>
                <Box display="flex" alignItems="center">
                  <CircleIcon
                    icon={faPenField}
                    backgroundColor={theme.palette.primary[300]}
                    color={theme.palette.primary[900]}
                    size="lg"
                    sx={{ marginRight: "14px" }}
                  />
                  <Typography color="textPrimary" variant="h2">
                    {editVehicleAlarmNotification
                      ? t("global.edit_notification", "Edit notification")
                      : t("global.add_notification", "Add notification")}
                  </Typography>
                </Box>
                <Typography
                  mt={3}
                  color="secondary"
                  variant="bodySmall"
                  style={{ color: theme.palette.grey[400] }}>
                  {t(
                    "vehiclealarm.users_who_are_added_will_receive",
                    "Users who are added will receive an email notification when an alarm is received on a vehicle that is connected to the company"
                  )}
                </Typography>
              </StyledListItemHeaderGrid>
            </Grid>
            <Box display="flex" flexDirection="column" gap={2} pb={5}>
              <AlarmTypeFormSelect t={t} />
              <UserFormAutoSelect usersResult={usersResult} t={t} />
              <CompanyFormAutoSelect companyResult={companyResult} t={t} />
              {editVehicleAlarmNotification && (
                <VehicleAlarmNotificationDelete
                  deleteNotificationSubmit={deleteNotificationSubmit}
                  result={deleteResult}
                  feedback={deleteFeedback}
                  id={formData?.id}
                />
              )}
            </Box>
          </form>
        )}
      </Formik>
    </NonFormikSidebar>
  ) : null
}
